/**
 * PRP-DAMAGE-1 (work item 5, D6): the interview's assumptions, on the project page under the
 * Claim facts section.
 *
 * Reads GET /mcp/projects/{id}/interview. The interview asks only a few questions (the budget);
 * every other question took its recommended choice and is listed here, grouped by room, with
 * the question, the choice and why. "Change" opens a small picker that posts
 * POST /mcp/projects/{id}/interview/answer {answers: [{id, choice}]} (the chat's
 * answer_open_items semantics) and refetches. Renders nothing on a 404, a failure, or when
 * there are no assumptions.
 */
import { useCallback, useEffect, useState } from 'react';

import { McpAPI } from '../../lib/mcpApi';
import type { InterviewAssumption, ProjectInterview } from '../../lib/mcpApi';
import { Banner } from './ui/banner';
import { Button } from './ui/button';

/** Props for {@link AssumptionsSummary}. */
export interface AssumptionsSummaryProps {
  projectId: string;
}

function messageOf(err: unknown): string {
  const m = (err as { message?: string } | undefined)?.message;
  return m && m.trim() ? m : 'That did not go through. Please try again.';
}

export default function AssumptionsSummary({ projectId }: AssumptionsSummaryProps) {
  const [data, setData] = useState<ProjectInterview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const got = await McpAPI.getInterview(projectId);
      setData(got && Array.isArray(got.assumptions) ? got : null);
    } catch {
      setData(null); // 404 (no interview, older server) or a failure: show nothing.
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const change = async (id: string, choice: number) => {
    setBusy(true);
    setError(null);
    try {
      await McpAPI.answerInterview(projectId, [{ id, choice }]);
      await load();
      return true;
    } catch (err) {
      setError(messageOf(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const groups = (data?.assumptions ?? []).filter((g) => (g.items ?? []).length > 0);
  if (groups.length === 0) return null;
  const count = groups.reduce((n, g) => n + g.items.length, 0);
  const b = data?.budget;

  return (
    <section
      className="mb-4 rounded-[var(--r-md)] border border-[var(--bd-subtle)] p-3"
      data-testid="assumptions"
      aria-label="Assumptions"
    >
      <h2 className="text-[14px] font-[600] text-[var(--tx-hi)]">Assumptions ({count})</h2>
      <p className="text-[11px] text-[var(--tx-faint)]">
        Settled with the recommended choice instead of a question. Change any of them here.
        {b && (
          <span data-testid="interview-budget">
            {' '}Asked: {b.asked} of {b.per_claim} (up to {b.per_room} per room).
          </span>
        )}
      </p>
      {error && (
        <Banner tone="warn" className="mt-2">
          {error}
        </Banner>
      )}
      {groups.map((g) => (
        <div key={g.group} className="mt-3">
          <h3 className="mb-1 text-[12px] font-[600] text-[var(--tx-lo)]">{g.group}</h3>
          <ul className="m-0 list-none space-y-2 p-0">
            {g.items.map((a) => (
              <AssumptionRow key={a.id} a={a} busy={busy} onChange={change} />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

interface AssumptionRowProps {
  a: InterviewAssumption;
  busy: boolean;
  onChange: (_id: string, _choice: number) => Promise<boolean>;
}

function AssumptionRow({ a, busy, onChange }: AssumptionRowProps) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<number>(a.choice);
  const choices = a.choices ?? [];

  return (
    <li className="break-words text-[13px] text-[var(--tx-hi)]" data-testid={`assumption-${a.id}`}>
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] text-[var(--tx-mid)]">{a.question}</span>
          <span className="font-[550]">{a.label}</span>
          {a.reason && <span className="block text-[11.5px] text-[var(--tx-lo)]">{a.reason}</span>}
        </span>
        {choices.length > 1 && !open && (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => {
              setPick(a.choice);
              setOpen(true);
            }}
            aria-label={`Change: ${a.question}`}
          >
            Change
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-1.5 space-y-1.5 border-l border-[var(--bd-subtle)] pl-3">
          <div role="radiogroup" aria-label={a.question} className="space-y-0.5">
            {choices.map((c) => (
              <label key={c.choice} className="flex cursor-pointer items-start gap-1.5 text-[12.5px]">
                <input
                  type="radio"
                  name={`as-${a.id}`}
                  value={c.choice}
                  checked={pick === c.choice}
                  onChange={() => setPick(c.choice)}
                  className="mt-[3px] accent-[var(--accent)]"
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={busy || pick === a.choice}
              onClick={() => void onChange(a.id, pick).then((ok) => ok && setOpen(false))}
              aria-label={`Save: ${a.question}`}
            >
              Save
            </Button>
            <Button size="sm" disabled={busy} onClick={() => setOpen(false)} aria-label={`Cancel: ${a.question}`}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
